import snakeCase from 'lodash/snakeCase'
import {
  alhenaChatAttachmentSchema,
  alhenaChatCloseSchema,
  alhenaChatOpenBubbleSchema,
  alhenaChatQueryValueSchema,
  alhenaChatSendMessageSchema,
  alhenaChatStartSchema,
  alhenaChatSuggestedCategoriesSchema,
  alhenaChatSuggestedCategoryClickSchema,
  alhenaChatSuggestedProductClickSchema,
  alhenaChatSuggestedProductsSchema,
} from '@integration-layer/schemas/GAEvents/alhenaChatEvents'

/**
 * Dispatches Google Analytics events for Alhena chat interactions.
 * Event IDs and payloads match the validated alhena_chat tracking specification.
 */
export const useGAAlhenaChatEvents = () => {
  const debug = !!useRuntimeConfig().public.ga4.debug

  const dispatchOpenChatBubble = () => {
    useGASendEvent(
      alhenaChatOpenBubbleSchema,
      {
        event: 'GAevent',
        eventID: 'C001',
        eventCategory: 'alhena_chat',
        eventAction: 'click_cta',
        eventLabel: 'open_chat_bubble',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  const dispatchCloseChat = () => {
    useGASendEvent(
      alhenaChatCloseSchema,
      {
        event: 'GAevent',
        eventID: 'C002',
        eventCategory: 'alhena_chat',
        eventAction: 'click_cta',
        eventLabel: 'close_chat',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  const dispatchStartChat = () => {
    useGASendEvent(
      alhenaChatStartSchema,
      {
        event: 'GAevent',
        eventID: 'C003',
        eventCategory: 'alhena_chat',
        eventAction: 'click_cta',
        eventLabel: 'start_chat',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  const dispatchQueryValue = (queryValue: string) => {
    useGASendEvent(
      alhenaChatQueryValueSchema,
      {
        event: 'GAevent',
        eventID: 'C004',
        eventCategory: 'alhena_chat',
        eventAction: 'text_input',
        eventLabel: 'query_value',
        eventElement01: queryValue,
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  const dispatchAddAttachment = (
    attachmentType: string,
    attachmentName: string
  ) => {
    useGASendEvent(
      alhenaChatAttachmentSchema,
      {
        event: 'GAevent',
        eventID: 'C005',
        eventCategory: 'alhena_chat',
        eventAction: 'click_cta',
        eventLabel: 'add_an_attachment',
        eventElement01: attachmentType,
        eventElement02: attachmentName,
        eventElement03: '-',
      },
      { debug }
    )
  }

  const dispatchSendMessage = (
    queryValue: string,
    inputMethod: 'button' | 'enter_key'
  ) => {
    useGASendEvent(
      alhenaChatSendMessageSchema,
      {
        event: 'GAevent',
        eventID: 'C006',
        eventCategory: 'alhena_chat',
        eventAction: 'click_cta',
        eventLabel: 'send_message',
        eventElement01: queryValue,
        eventElement02: inputMethod,
        eventElement03: '-',
      },
      { debug }
    )
  }

  const dispatchSuggestedCategories = (categoriesList: string) => {
    useGASendEvent(
      alhenaChatSuggestedCategoriesSchema,
      {
        event: 'GAevent',
        eventID: 'C007',
        eventCategory: 'alhena_chat',
        eventAction: 'element_visibility',
        eventLabel: 'suggested_categories',
        eventElement01: categoriesList,
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  const dispatchSuggestedCategoryClick = (
    categoriesList: string,
    categoryClicked: string
  ) => {
    useGASendEvent(
      alhenaChatSuggestedCategoryClickSchema,
      {
        event: 'GAevent',
        eventID: 'C008',
        eventCategory: 'alhena_chat',
        eventAction: 'click_cta',
        eventLabel: 'suggested_category_click',
        eventElement01: categoriesList,
        eventElement02: categoryClicked,
        eventElement03: '-',
      },
      { debug }
    )
  }

  const dispatchSuggestedProducts = (
    categorySelected: string,
    suggestedProductIds: string
  ) => {
    useGASendEvent(
      alhenaChatSuggestedProductsSchema,
      {
        event: 'GAevent',
        eventID: 'C009',
        eventCategory: 'alhena_chat',
        eventAction: 'element_visibility',
        eventLabel: 'suggested_products',
        eventElement01: categorySelected,
        eventElement02: suggestedProductIds,
        eventElement03: '-',
      },
      { debug }
    )
  }

  const dispatchSuggestedProductClick = (
    categorySelected: string,
    suggestedProductIds: string,
    productClickedId: string
  ) => {
    useGASendEvent(
      alhenaChatSuggestedProductClickSchema,
      {
        event: 'GAevent',
        eventID: 'C010',
        eventCategory: 'alhena_chat',
        eventAction: 'select_item',
        eventLabel: 'suggested_product_click',
        eventElement01: categorySelected,
        eventElement02: suggestedProductIds,
        eventElement03: productClickedId,
      },
      { debug }
    )
  }

  return {
    dispatchOpenChatBubble,
    dispatchCloseChat,
    dispatchStartChat,
    dispatchQueryValue,
    dispatchAddAttachment,
    dispatchSendMessage,
    dispatchSuggestedCategories,
    dispatchSuggestedCategoryClick,
    dispatchSuggestedProducts,
    dispatchSuggestedProductClick,
    formatCategoryLabel: (label: string) => snakeCase(label),
    formatCategoryList: (categories: string[]) =>
      categories.map(category => snakeCase(category)).join('|'),
  }
}
